/* global $ */
import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Footer from '../components/Footer';

function User() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [userTypes, setUserTypes] = useState([]); // For user type dropdown
    const [userRoles, setUserRoles] = useState([]); // For user role dropdown
    const [departments, setDepartments] = useState([]); // For department dropdown
    const [loading, setLoading] = useState(true);

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    // Form state for add/edit user
    const [formData, setFormData] = useState({
        user_name: '',
        email_id: '',
        phone_no: '',
        user_type_id: '',
        roll_id: '',
        department_id: '',
        profile_picture_url: '',
        isactive: true
    });

    // React Table State
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Advanced filters
    const [filters, setFilters] = useState({
        user_name: '',
        email_id: '',
        phone_no: '',
        user_type_id: '',
        roll_id: '',
        department_id: '',
        isactive: ''
    });

    // Generate random password
    const generateRandomPassword = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
        let password = '';
        for (let i = 0; i < 12; i++) {
            password += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return password;
    };

    // Filter users based on search term
    const filteredUsers = users.filter(user =>
        user.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.user_type?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.user_role?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.department?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Apply advanced filters if any are set
    const advancedFilteredUsers = filteredUsers.filter(user => {
        return (
            (filters.user_name === '' ||
                user.user_name?.toLowerCase().includes(filters.user_name.toLowerCase())) &&
            (filters.email_id === '' ||
                user.email_id?.toLowerCase().includes(filters.email_id.toLowerCase())) &&
            (filters.phone_no === '' ||
                user.phone_no?.includes(filters.phone_no)) &&
            (filters.user_type_id === '' ||
                user.user_type_id == filters.user_type_id) &&
            (filters.roll_id === '' ||
                user.roll_id == filters.roll_id) &&
            (filters.department_id === '' ||
                user.department_id == filters.department_id) &&
            (filters.isactive === '' ||
                user.isactive.toString() === filters.isactive)
        );
    });

    // Sort filtered users
    const sortedUsers = React.useMemo(() => {
        let sortableUsers = [...advancedFilteredUsers];
        if (sortConfig.key) {
            sortableUsers.sort((a, b) => {
                let aValue;
                let bValue;

                switch (sortConfig.key) {
                    case 'user_type_name':
                        aValue = a.user_type?.name || '';
                        bValue = b.user_type?.name || '';
                        break;
                    case 'role_name':
                        aValue = a.user_role?.name || '';
                        bValue = b.user_role?.name || '';
                        break;
                    case 'department_name':
                        aValue = a.department?.name || '';
                        bValue = b.department?.name || '';
                        break;
                    default:
                        aValue = a[sortConfig.key] || '';
                        bValue = b[sortConfig.key] || '';
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableUsers;
    }, [advancedFilteredUsers, sortConfig]);

    // Calculate pagination
    const totalItems = sortedUsers.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentUsers = sortedUsers.slice(startIndex, endIndex);

    // Handle sorting
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Get sort icon
    const getSortIcon = (columnKey) => {
        if (sortConfig.key === columnKey) {
            return sortConfig.direction === 'asc' ? '↑' : '↓';
        }
        return '';
    };

    const handleNavigatePermissions = (user) => {
        navigate(`/permissions/${user.id}`);
    };

    // Reset pagination when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Fetch Users from API
    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get("/users/details/all");
            console.log("Users response:", response.data);
            setUsers(response.data.data || response.data);
        } catch (error) {
            console.error("Error fetching Users:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'Failed to fetch users data'
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch User Types for filter dropdown
    const fetchUserTypes = async () => {
        try {
            const response = await axiosInstance.get("/usertype/");
            setUserTypes(response.data.data || response.data);
        } catch (error) {
            console.error("Error fetching User Types:", error);
        }
    };

    // Fetch User Roles for filter dropdown
    const fetchUserRoles = async () => {
        try {
            const response = await axiosInstance.get("/userroll/");
            setUserRoles(response.data.data || response.data);
        } catch (error) {
            console.error("Error fetching User Roles:", error);
        }
    };

    // Fetch Departments for filter dropdown
    const fetchDepartments = async () => {
        try {
            const response = await axiosInstance.get("/department/");
            setDepartments(response.data.data || response.data);
        } catch (error) {
            console.error("Error fetching Departments:", error);
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchUserTypes();
        fetchUserRoles();
        fetchDepartments();
    }, []);

    // Handle filter changes
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Clear all filters
    const clearFilters = () => {
        setFilters({
            user_name: '',
            email_id: '',
            phone_no: '',
            user_type_id: '',
            roll_id: '',
            department_id: '',
            isactive: ''
        });
        setSearchTerm('');
    };

    // Navigate to User Details page
    const handleViewDetails = (user) => {
        setSelectedUser(user);
        setShowViewModal(true);
    };

    // Handle form input changes
    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Reset form data
    const resetFormData = () => {
        setFormData({
            user_name: '',
            email_id: '',
            phone_no: '',
            user_type_id: '',
            roll_id: '',
            department_id: '',
            profile_picture_url: '',
            isactive: true
        });
    };

    // Handle Add User
    const handleAddUser = () => {
        resetFormData();
        setShowAddModal(true);
    };

    // Handle Edit User
    const handleEdit = (user) => {
        console.log("user data:", user);
        setSelectedUser(user);
        setFormData({
            user_name: user.user_name || '',
            email_id: user.email_id || '',
            phone_no: user.phone_no || '',
            user_type_id: user.user_type_id || '',
            roll_id: user.roll_id || '',
            department_id: user.department_id || '',
            profile_picture_url: user.profile_picture_url || '',
            isactive: user.isactive !== undefined ? user.isactive : true
        });
        setShowEditModal(true);
    };

    // Handle Reset Password
    const handleResetPassword = async (user) => {
        const result = await Swal.fire({
            title: 'Reset Password',
            text: `Are you sure you want to reset the password for ${user.user_name}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, Reset Password',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const newPassword = generateRandomPassword();

                await axiosInstance.put(`/users/resetpassword/${user.id}`, {
                    password_hash: newPassword,
                    force_password_change: true
                });

                await Swal.fire({
                    title: 'Password Reset Successfully!',
                    html: `
                        <div class="text-left">
                            <p><strong>User:</strong> ${user.user_name}</p>
                            <p><strong>New Password:</strong></p>
                            <div class="alert alert-info">
                                <code style="font-size: 14px; font-weight: bold;">${newPassword}</code>
                            </div>
                            <p class="text-warning"><small>
                                <i class="fas fa-exclamation-triangle"></i>
                                Please share this password securely with the user. The user will be required to change this password on next login.
                            </small></p>
                        </div>
                    `,
                    icon: 'success',
                    confirmButtonText: 'Copy Password',
                    showCancelButton: true,
                    cancelButtonText: 'Close'
                }).then((result) => {
                    if (result.isConfirmed) {
                        // Copy password to clipboard
                        navigator.clipboard.writeText(newPassword).then(() => {
                            Swal.fire({
                                title: 'Password Copied!',
                                text: 'The password has been copied to your clipboard.',
                                icon: 'success',
                                timer: 2000,
                                showConfirmButton: false
                            });
                        });
                    }
                });

                fetchUsers(); // Refresh the list
            } catch (error) {
                console.error('Error resetting password:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: error.response?.data?.message || 'Failed to reset password'
                });
            }
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic validation
        if (!formData.user_name || !formData.email_id) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'User name and email are required!'
            });
            return;
        }

        try {
            const submitData = { ...formData };

            if (showAddModal) {
                // Generate random password for new user
                const randomPassword = generateRandomPassword();
                submitData.password = randomPassword;
                submitData.force_password_change = true;

                const response = await axiosInstance.post('/users/', submitData);

                await Swal.fire({
                    title: 'User Created Successfully!',
                    html: `
                        <div class="text-left">
                            <p><strong>User:</strong> ${submitData.user_name}</p>
                            <p><strong>Email:</strong> ${submitData.email_id}</p>
                            <p><strong>Generated Password:</strong></p>
                            <div class="alert alert-info">
                                <code style="font-size: 14px; font-weight: bold;">${randomPassword}</code>
                            </div>
                            <p class="text-warning"><small>
                                <i class="fas fa-exclamation-triangle"></i>
                                Please share this password securely with the user. The user will be required to change this password on first login.
                            </small></p>
                        </div>
                    `,
                    icon: 'success',
                    confirmButtonText: 'Copy Password',
                    showCancelButton: true,
                    cancelButtonText: 'Close'
                }).then((result) => {
                    if (result.isConfirmed) {
                        // Copy password to clipboard
                        navigator.clipboard.writeText(randomPassword).then(() => {
                            Swal.fire({
                                title: 'Password Copied!',
                                text: 'The password has been copied to your clipboard.',
                                icon: 'success',
                                timer: 2000,
                                showConfirmButton: false
                            });
                        });
                    }
                });

                setShowAddModal(false);
            } else {
                await axiosInstance.put(`/users/${selectedUser.id}`, submitData);
                Swal.fire('Success!', 'User has been updated.', 'success');
                setShowEditModal(false);
            }

            resetFormData();
            fetchUsers(); // Refresh the list
        } catch (error) {
            console.error('Error saving user:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: error.response?.data?.message || 'Failed to save user'
            });
        }
    };

    // Delete User
    const handleDelete = async (user) => {
        setSelectedUser(user);
        setShowDeleteModal(true);
    };

    // Confirm delete
    const confirmDelete = async () => {
        try {
            await axiosInstance.delete(`/users/${selectedUser.id}`);
            Swal.fire('Deleted!', 'User has been deleted.', 'success');
            setShowDeleteModal(false);
            setSelectedUser(null);
            fetchUsers(); // Refresh the list
        } catch (error) {
            console.error('Error deleting user:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'Failed to delete user'
            });
        }
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Format last login
    const formatLastLogin = (dateString) => {
        if (!dateString) return 'Never';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 60) {
            return `${diffMins} mins ago`;
        } else if (diffHours < 24) {
            return `${diffHours} hours ago`;
        } else if (diffDays < 7) {
            return `${diffDays} days ago`;
        } else {
            return formatDate(dateString);
        }
    };

    // Pagination component
    const Pagination = () => {
        const pageNumbers = [];
        const maxVisiblePages = 5;

        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                    Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
                    {filteredUsers.length !== users.length && ` (filtered from ${users.length} total entries)`}
                </div>
                <div className="d-flex align-items-center">
                    <nav>
                        <ul className="pagination">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                >
                                    <i className="fa fa-angle-double-left me-2"></i> First
                                </button>
                            </li>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <i className="fa fa-angle-double-left me-2"></i> Previous
                                </button>
                            </li>
                            {pageNumbers.map(number => (
                                <li key={number} className={`page-item ${number === currentPage ? 'active' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(number)}
                                    >
                                        {number}
                                    </button>
                                </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next <i className="fa fa-angle-double-right ms-2"></i>
                                </button>
                            </li>
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                >
                                    Last
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
        );
    };

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">
                    {/* Page Header */}
                    <div className="page-header">
                        <div className="content-page-header">
                            <h5>Users</h5>
                            <div className="list-btn">
                                <ul className="filter-list">
                                    <li>
                                        <button
                                            className="btn btn-filters w-auto"
                                            onClick={() => $('#filter_inputs').toggle()}
                                            data-bs-toggle="tooltip"
                                            data-bs-placement="bottom"
                                            title="Filter"
                                        >
                                            <span className="me-2">
                                                <img src="assets/img/icons/filter-icon.svg" alt="filter" />
                                            </span>Filter
                                        </button>
                                    </li>
                                    <li>
                                        <div className="dropdown dropdown-action" data-bs-toggle="tooltip" data-bs-placement="top" title="Download">
                                            <a href="#" className="btn-filters" data-bs-toggle="dropdown" aria-expanded="false">
                                                <span><i className="fe fe-download"></i></span>
                                            </a>
                                            <div className="dropdown-menu dropdown-menu-end">
                                                <ul className="d-block">
                                                    <li>
                                                        <a className="d-flex align-items-center download-item" href="javascript:void(0);">
                                                            <i className="far fa-file-pdf me-2"></i>PDF
                                                        </a>
                                                    </li>
                                                    <li>
                                                        <a className="d-flex align-items-center download-item" href="javascript:void(0);">
                                                            <i className="far fa-file-text me-2"></i>CSV
                                                        </a>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </li>
                                    <li>
                                        <a className="btn-filters" href="javascript:void(0);" data-bs-toggle="tooltip" data-bs-placement="bottom" title="Print">
                                            <span><i className="fe fe-printer"></i></span>
                                        </a>
                                    </li>
                                    <li>
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleAddUser}
                                        >
                                            <i className="fa fa-plus-circle me-2" aria-hidden="true"></i>Add User
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    {/* /Page Header */}

                    {/* Search Filter */}
                    <div id="filter_inputs" className="card filter-card" style={{ display: 'none' }}>
                        <div className="card-body pb-0">
                            <div className="row">
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>User Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="user_name"
                                            value={filters.user_name}
                                            onChange={handleFilterChange}
                                            placeholder="Search by user name"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Email</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="email_id"
                                            value={filters.email_id}
                                            onChange={handleFilterChange}
                                            placeholder="Search by email"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Phone</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="phone_no"
                                            value={filters.phone_no}
                                            onChange={handleFilterChange}
                                            placeholder="Search by phone"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>User Type</label>
                                        <select
                                            className="form-control form-select"
                                            name="user_type_id"
                                            value={filters.user_type_id}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">All User Types</option>
                                            {userTypes.map(type => (
                                                <option key={type.id} value={type.id}>
                                                    {type.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Role</label>
                                        <select
                                            className="form-control form-select"
                                            name="roll_id"
                                            value={filters.roll_id}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">All Roles</option>
                                            {userRoles.map(role => (
                                                <option key={role.id} value={role.id}>
                                                    {role.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Department</label>
                                        <select
                                            className="form-control form-select"
                                            name="department_id"
                                            value={filters.department_id}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">All Departments</option>
                                            {departments.map(dept => (
                                                <option key={dept.id} value={dept.id}>
                                                    {dept.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Status</label>
                                        <select
                                            className="form-control form-select"
                                            name="isactive"
                                            value={filters.isactive}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">All Status</option>
                                            <option value="true">Active</option>
                                            <option value="false">Inactive</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-sm-12">
                                    <div className="input-block mb-3 text-end">
                                        <button
                                            type="button"
                                            className="btn btn-secondary me-2"
                                            onClick={clearFilters}
                                        >
                                            Clear Filters
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* /Search Filter */}

                    {/* Table */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card-table">
                                <div className="card-body">
                                    {/* Table Controls */}
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex align-items-center">
                                            <label className="me-2">Show</label>
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ width: 'auto' }}
                                                value={itemsPerPage}
                                                onChange={(e) => {
                                                    setItemsPerPage(Number(e.target.value));
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>
                                            <label className="ms-2">entries</label>
                                        </div>
                                        <div>
                                            <label className="me-2">Search:</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm d-inline-block"
                                                style={{ width: '200px' }}
                                                placeholder="Search users..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-center table-hover">
                                            <thead className="thead-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('user_name')}
                                                    >
                                                        Username {getSortIcon('user_name')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('phone_no')}
                                                    >
                                                        Mobile Number {getSortIcon('phone_no')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('email')}
                                                    >
                                                        Email {getSortIcon('email')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('last_login_at')}
                                                    >
                                                        Last Activity {getSortIcon('last_login_at')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('createdAt')}
                                                    >
                                                        Created on {getSortIcon('createdAt')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('isactive')}
                                                    >
                                                        Status {getSortIcon('isactive')}
                                                    </th>
                                                    <th className="no-sort">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan="8" className="text-center">
                                                            <div className="spinner-border" role="status">
                                                                <span className="visually-hidden">Loading...</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : currentUsers.length > 0 ? (
                                                    currentUsers.map((user, index) => (
                                                        <tr key={user.id}>
                                                            <td>{startIndex + index + 1}</td>
                                                            <td>
                                                                <h2 className="table-avatar">
                                                                    <a href="#" className="avatar avatar-sm me-2">
                                                                        <img
                                                                            className="avatar-img rounded-circle"
                                                                            src={user.profile_picture_url || "assets/img/profiles/avatar-14.jpg"}
                                                                            alt="User Image"
                                                                        />
                                                                    </a>
                                                                    <span
                                                                        style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                                                                        onClick={() => handleViewDetails(user)}
                                                                        title="Click to view details"
                                                                    >
                                                                        {user.user_name || 'N/A'}
                                                                        {user.email_id && (
                                                                            <span>
                                                                                <span> {user.email_id}</span>
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                </h2>
                                                            </td>
                                                            <td>{user.phone_no || 'N/A'}</td>
                                                            <td>{user.user_role?.name || 'N/A'}</td>
                                                            <td>
                                                                <span className={`badge ${user.last_login_at ? 'bg-success-light' : 'bg-ash-gray text-gray-light'}`}>
                                                                    {formatLastLogin(user.last_login_at)}
                                                                </span>
                                                            </td>
                                                            <td>{formatDate(user.createdAt)}</td>
                                                            <td>
                                                                <span className={`badge ${user.isactive ? 'bg-success-light' : 'bg-ash-gray text-gray-light'}`}>
                                                                    {user.isactive ? 'Active' : 'Restricted'}
                                                                </span>
                                                            </td>
                                                            <td className="d-flex align-items-center">
                                                                <a
                                                                    className="btn-action-icon bg-info-light me-2"
                                                                    onClick={() => handleNavigatePermissions(user)}
                                                                    title="Manage Permissions"
                                                                >
                                                                    <i className="fas fa-user-shield me-2"></i>
                                                                </a>
                                                                <a className="btn-action-icon bg-primary-light me-2"
                                                                    onClick={() => handleViewDetails(user)}
                                                                    title="View Details"
                                                                >
                                                                    <i className="far fa-eye me-2"></i>
                                                                </a>
                                                                <a className="btn-action-icon bg-success-light me-2"
                                                                    onClick={() => handleEdit(user)}
                                                                    title="Edit User"
                                                                >
                                                                    <i className="far fa-edit me-2"></i>
                                                                </a>
                                                                <a className="btn-action-icon bg-warning-light me-2"
                                                                    onClick={() => handleResetPassword(user)}
                                                                    title="Reset Password"
                                                                >
                                                                    <i className="fas fa-key me-2"></i>
                                                                </a>
                                                                <a className="btn-action-icon btn-greys bg-danger-light"
                                                                    onClick={() => handleDelete(user)}
                                                                    title="Delete User"
                                                                >
                                                                    <i className="far fa-trash-alt me-2"></i>
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="8" className="text-center">
                                                            {searchTerm ? "No matching users found" : "No users available in table"}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {totalItems > 0 && <Pagination />}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* /Table */}
                </div>
                <Footer />
            </div>

            {/* View User Modal */}
            <div className={`modal fade ${showViewModal ? 'show' : ''}`}
                id="view_user"
                tabIndex="-1"
                style={{ display: showViewModal ? 'block' : 'none' }}
                aria-hidden={!showViewModal}>
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">User Details</h4>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => setShowViewModal(false)}
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            {selectedUser && (
                                <div className="row">
                                    <div className="col-md-12 text-center mb-4">
                                        <div className="profile-img">
                                            <img
                                                src={selectedUser.profile_picture_url || "assets/img/profiles/avatar-14.jpg"}
                                                alt="User Profile"
                                                className="rounded-circle"
                                                style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                                            />
                                        </div>
                                        <h3 className="mt-3">{selectedUser.user_name}</h3>
                                        <span className={`badge ${selectedUser.isactive ? 'bg-success' : 'bg-danger'} fs-6`}>
                                            {selectedUser.isactive ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group mb-3">
                                            <label className="fw-bold">User Name:</label>
                                            <p className="form-control-plaintext">{selectedUser.user_name || 'N/A'}</p>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group mb-3">
                                            <label className="fw-bold">Email:</label>
                                            <p className="form-control-plaintext">
                                                {selectedUser.email_id ? (
                                                    <a href={`mailto:${selectedUser.email_id}`} className="text-primary">
                                                        {selectedUser.email_id}
                                                    </a>
                                                ) : 'N/A'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group mb-3">
                                            <label className="fw-bold">Phone Number:</label>
                                            <p className="form-control-plaintext">
                                                {selectedUser.phone_no ? (
                                                    <a href={`tel:${selectedUser.phone_no}`} className="text-primary">
                                                        {selectedUser.phone_no}
                                                    </a>
                                                ) : 'N/A'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group mb-3">
                                            <label className="fw-bold">User Type:</label>
                                            <p className="form-control-plaintext">{selectedUser.user_type?.name || 'N/A'}</p>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group mb-3">
                                            <label className="fw-bold">Role:</label>
                                            <p className="form-control-plaintext">{selectedUser.user_role?.name || 'N/A'}</p>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group mb-3">
                                            <label className="fw-bold">Department:</label>
                                            <p className="form-control-plaintext">{selectedUser.department?.name || 'N/A'}</p>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group mb-3">
                                            <label className="fw-bold">Last Login:</label>
                                            <p className="form-control-plaintext">
                                                <span className={`badge ${selectedUser.last_login_at ? 'bg-success-light' : 'bg-ash-gray text-gray-light'}`}>
                                                    {formatLastLogin(selectedUser.last_login_at)}
                                                </span>
                                            </p>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group mb-3">
                                            <label className="fw-bold">Created On:</label>
                                            <p className="form-control-plaintext">{formatDate(selectedUser.createdAt)}</p>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group mb-3">
                                            <label className="fw-bold">Last Updated:</label>
                                            <p className="form-control-plaintext">{formatDate(selectedUser.updatedAt)}</p>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group mb-3">
                                            <label className="fw-bold">Password Changed:</label>
                                            <p className="form-control-plaintext">{formatDate(selectedUser.password_changed_at)}</p>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group mb-3">
                                            <label className="fw-bold">Force Password Change:</label>
                                            <p className="form-control-plaintext">
                                                <span className={`badge ${selectedUser.force_password_change ? 'bg-warning' : 'bg-success-light'}`}>
                                                    {selectedUser.force_password_change ? 'Required' : 'Not Required'}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-warning me-2"
                                onClick={() => {
                                    setShowViewModal(false);
                                    handleResetPassword(selectedUser);
                                }}
                            >
                                <i className="fas fa-key me-2"></i>Reset Password
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary me-2"
                                onClick={() => {
                                    setShowViewModal(false);
                                    handleEdit(selectedUser);
                                }}
                            >
                                <i className="far fa-edit me-2"></i>Edit User
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setShowViewModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add User Modal */}
            <div className={`modal fade ${showAddModal ? 'show' : ''}`}
                id="add_user"
                tabIndex="-1"
                style={{ display: showAddModal ? 'block' : 'none' }}
                aria-hidden={!showAddModal}>
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">Add User</h4>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => setShowAddModal(false)}
                                aria-label="Close"
                            ></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="alert alert-info">
                                    <i className="fas fa-info-circle me-2"></i>
                                    A random password will be generated automatically for the new user. The password will be displayed after successful creation.
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>User Name <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="user_name"
                                                value={formData.user_name}
                                                onChange={handleFormChange}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>Email <span className="text-danger">*</span></label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                name="email_id"
                                                value={formData.email_id}
                                                onChange={handleFormChange}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>Phone Number</label>
                                            <input
                                                type="tel"
                                                className="form-control"
                                                name="phone_no"
                                                value={formData.phone_no}
                                                onChange={handleFormChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>User Type</label>
                                            <select
                                                className="form-control form-select"
                                                name="user_type_id"
                                                value={formData.user_type_id}
                                                onChange={handleFormChange}
                                            >
                                                <option value="">Select User Type</option>
                                                {userTypes.map(type => (
                                                    <option key={type.id} value={type.id}>
                                                        {type.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>Role</label>
                                            <select
                                                className="form-control form-select"
                                                name="roll_id"
                                                value={formData.roll_id}
                                                onChange={handleFormChange}
                                            >
                                                <option value="">Select Role</option>
                                                {userRoles.map(role => (
                                                    <option key={role.id} value={role.id}>
                                                        {role.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>Department</label>
                                            <select
                                                className="form-control form-select"
                                                name="department_id"
                                                value={formData.department_id}
                                                onChange={handleFormChange}
                                            >
                                                <option value="">Select Department</option>
                                                {departments.map(dept => (
                                                    <option key={dept.id} value={dept.id}>
                                                        {dept.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-12">
                                        <div className="input-block mb-3">
                                            <label>Profile Picture URL</label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                name="profile_picture_url"
                                                value={formData.profile_picture_url}
                                                onChange={handleFormChange}
                                                placeholder="https://example.com/image.jpg"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-12">
                                        <div className="input-block mb-3">
                                            <div className="form-check form-switch">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    name="isactive"
                                                    id="isactive_add"
                                                    checked={formData.isactive}
                                                    onChange={handleFormChange}
                                                />
                                                <label className="form-check-label" htmlFor="isactive_add">
                                                    {formData.isactive ? 'Active' : 'Inactive'}
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary me-2"
                                    onClick={() => setShowAddModal(false)}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    <i className="fas fa-user-plus me-2"></i>Save User
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Edit User Modal */}
            <div className={`modal fade ${showEditModal ? 'show' : ''}`}
                id="edit_user"
                tabIndex="-1"
                style={{ display: showEditModal ? 'block' : 'none' }}
                aria-hidden={!showEditModal}>
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">Edit User</h4>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => setShowEditModal(false)}
                                aria-label="Close"
                            ></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>User Name <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="user_name"
                                                value={formData.user_name}
                                                onChange={handleFormChange}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>Email <span className="text-danger">*</span></label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                name="email_id"
                                                value={formData.email_id}
                                                onChange={handleFormChange}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>Phone Number</label>
                                            <input
                                                type="tel"
                                                className="form-control"
                                                name="phone_no"
                                                value={formData.phone_no}
                                                onChange={handleFormChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>User Type</label>
                                            <select
                                                className="form-control form-select"
                                                name="user_type_id"
                                                value={formData.user_type_id}
                                                onChange={handleFormChange}
                                            >
                                                <option value="">Select User Type</option>
                                                {userTypes.map(type => (
                                                    <option key={type.id} value={type.id}>
                                                        {type.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>Role</label>
                                            <select
                                                className="form-control form-select"
                                                name="roll_id"
                                                value={formData.roll_id}
                                                onChange={handleFormChange}
                                            >
                                                <option value="">Select Role</option>
                                                {userRoles.map(role => (
                                                    <option key={role.id} value={role.id}>
                                                        {role.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="input-block mb-3">
                                            <label>Department</label>
                                            <select
                                                className="form-control form-select"
                                                name="department_id"
                                                value={formData.department_id}
                                                onChange={handleFormChange}
                                            >
                                                <option value="">Select Department</option>
                                                {departments.map(dept => (
                                                    <option key={dept.id} value={dept.id}>
                                                        {dept.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-12">
                                        <div className="input-block mb-3">
                                            <label>Profile Picture URL</label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                name="profile_picture_url"
                                                value={formData.profile_picture_url}
                                                onChange={handleFormChange}
                                                placeholder="https://example.com/image.jpg"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-12">
                                        <div className="input-block mb-3">
                                            <div className="form-check form-switch">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    name="isactive"
                                                    id="isactive_edit"
                                                    checked={formData.isactive}
                                                    onChange={handleFormChange}
                                                />
                                                <label className="form-check-label" htmlFor="isactive_edit">
                                                    {formData.isactive ? 'Active' : 'Inactive'}
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-warning me-2"
                                    onClick={() => {
                                        setShowEditModal(false);
                                        handleResetPassword(selectedUser);
                                    }}
                                >
                                    <i className="fas fa-key me-2"></i>Reset Password
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary me-2"
                                    onClick={() => setShowEditModal(false)}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    <i className="fas fa-user-edit me-2"></i>Update User
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <div className={`modal fade ${showDeleteModal ? 'show' : ''}`}
                id="delete_modal"
                tabIndex="-1"
                style={{ display: showDeleteModal ? 'block' : 'none' }}
                aria-hidden={!showDeleteModal}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">Delete User</h4>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => setShowDeleteModal(false)}
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            <div className="delete-action">
                                <div className="delete-header">
                                    <h4>Are you sure you want to delete this user?</h4>
                                    <p>This action cannot be undone.</p>
                                </div>
                                {selectedUser && (
                                    <div className="user-info mt-3">
                                        <p><strong>User:</strong> {selectedUser.user_name}</p>
                                        <p><strong>Email:</strong> {selectedUser.email_id}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary me-2"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={confirmDelete}
                            >
                                Delete User
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal backdrop */}
            {(showAddModal || showEditModal || showViewModal || showDeleteModal) && (
                <div className="modal-backdrop fade show"></div>
            )}
        </div>
    );
}

export default User;